import type { OrganizationSettings, OrganizationSettingsRequest } from "@shyne/shared/api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getCurrentOrganization,
  updateCurrentOrganization,
  uploadOrganizationImage,
} from "@/app/organization/settings/organization/organization.api.ts";

interface OrganizationState {
  data: OrganizationSettings | null;
  loading: boolean;
  saving: boolean;
  uploadingAvatar: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  data: null,
  loading: false,
  saving: false,
  uploadingAvatar: false,
  error: null,
};

export const fetchOrganization = createAsyncThunk("organization/fetch", async () => await getCurrentOrganization());

export const updateOrganization = createAsyncThunk(
  "organization/update",
  async (settings: OrganizationSettingsRequest) => await updateCurrentOrganization(settings)
);

export const uploadOrgAvatar = createAsyncThunk("organization/uploadAvatar", async (file: File) => {
  return await uploadOrganizationImage(file);
});

const slice = createSlice({
  name: "organization",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganization.pending, (state) => {
        state.loading = true;
        state.data = null;
      })
      .addCase(fetchOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to fetch organization.";
      })
      .addCase(updateOrganization.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.saving = false;
        state.data = action.payload;
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? "Failed to update organization.";
      })
      .addCase(uploadOrgAvatar.pending, (state) => {
        state.uploadingAvatar = true;
      })
      .addCase(uploadOrgAvatar.fulfilled, (state, action) => {
        state.uploadingAvatar = false;
        state.data = action.payload;
      })
      .addCase(uploadOrgAvatar.rejected, (state, action) => {
        state.uploadingAvatar = false;
        state.error = action.error.message ?? null;
      });
  },
});

export default slice.reducer;
